import {useEffect, useRef} from 'react';
import {useSearchParams} from 'react-router-dom';
import {useShallow} from 'zustand/react/shallow';
import type {BaseFilterOption} from '@/types/models/creator-crm/filter';
import useContentSubmissionsStore, {
  contentSubmissionsActions,
} from '@/stores/content/content-submissions/contentSubmissionsStore';
import {CONTENT_TYPE_OPTIONS} from '@/stores/content/content-submissions/filterConfig';
import type {CampaignCreator} from '../types/campaignCreator';

type UseRestoreFiltersFromUrlParams = {
  campaignCreators: CampaignCreator[];
  isLoadingContracts: boolean;
  statusOptions: BaseFilterOption[];
};

/**
 * Two-way sync between URL search params and the content submissions
 * filter store.
 *
 * **Restore** — on first load, reads `kind`, `profile_id`, and `status`
 * from the URL and pushes them into the Zustand store.  Waits for
 * `campaignCreators` to be available before resolving `profile_id`.
 *
 * **Sync back** — whenever `appliedFilters` change, writes them back to
 * the URL so the filter state is shareable / survives refresh.
 *
 */
function useRestoreFiltersFromUrl({
  campaignCreators,
  isLoadingContracts,
  statusOptions,
}: UseRestoreFiltersFromUrlParams) {
  const [searchParams, setSearchParams] = useSearchParams();

  const appliedFilters = useContentSubmissionsStore(
    useShallow((state) => state.appliedFilters)
  );

  const restoreStateRef = useRef<'pending' | 'restoring' | 'done'>('pending');

  useEffect(
    function restoreAppliedFiltersFromUrlSearchParams() {
      if (restoreStateRef.current !== 'pending') return;

      const profileIdParam = searchParams.get('profile_id');

      if (profileIdParam && isLoadingContracts) return;

      restoreStateRef.current = 'restoring';

      const kindParam = searchParams.get('kind');
      const statusParam = searchParams.get('status');

      if (kindParam || profileIdParam || statusParam) {
        const restoredContentType = kindParam
          ? (kindParam
              .split(',')
              .map((id) =>
                CONTENT_TYPE_OPTIONS.find((option) => option.id === id)
              )
              .filter(Boolean) as typeof appliedFilters.contentType)
          : [];

        const restoredTalent = profileIdParam
          ? (campaignCreators.find(
              (creator) => creator.id === profileIdParam
            ) ?? null)
          : null;

        const restoredStatus = statusParam
          ? (statusParam
              .split(',')
              .map((id) => statusOptions.find((option) => option.id === id))
              .filter(Boolean) as typeof appliedFilters.status)
          : [];

        contentSubmissionsActions.restoreFilters({
          contentType: restoredContentType,
          talent: restoredTalent,
          status: restoredStatus,
        });
      }
      restoreStateRef.current = 'done';
    },
    [isLoadingContracts, campaignCreators, statusOptions, searchParams]
  );

  useEffect(
    function setUrlSearchParamsFromAppliedFilters() {
      if (restoreStateRef.current !== 'done') return;

      const syncParam = (
        params: URLSearchParams,
        key: string,
        value: string | null
      ) => {
        if (value) {
          params.set(key, value);
        } else {
          params.delete(key);
        }
      };

      setSearchParams(
        (prev) => {
          syncParam(
            prev,
            'kind',
            appliedFilters.contentType.map((o) => o.id).join(',') || null
          );
          syncParam(prev, 'profile_id', appliedFilters.talent?.id ?? null);
          syncParam(
            prev,
            'status',
            appliedFilters.status.map((s) => s.id).join(',') || null
          );

          return prev;
        },
        {replace: true}
      );
    },
    [appliedFilters, setSearchParams]
  );
}

export default useRestoreFiltersFromUrl;
